import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { apiAdmin } from '../../services/apiService';
import { ApiDetails, ApiFieldMapping } from '../../types';

interface FieldMapping extends ApiFieldMapping {
  // Extends the imported type
}

interface FieldMappingManagerProps {
  apiId?: number;
  onClose?: () => void;
}

export const FieldMappingManager: React.FC<FieldMappingManagerProps> = ({ apiId, onClose }) => {
  const [apis, setApis] = useState<ApiDetails[]>([]);
  const [selectedApiId, setSelectedApiId] = useState<number | ''>(apiId || '');
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingMapping, setEditingMapping] = useState<FieldMapping | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Load APIs
  useEffect(() => {
    loadApis();
  }, []);

  // Load field mappings when API changes
  useEffect(() => {
    if (selectedApiId) {
      loadFieldMappings(selectedApiId);
    } else {
      setFieldMappings([]);
    }
  }, [selectedApiId]);

  const loadApis = async () => {
    try {
      setLoading(true);
      const apiList = await apiAdmin.listApis();
      setApis(apiList);
    } catch (err) {
      setError('Failed to load APIs');
      console.error('Error loading APIs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFieldMappings = async (apiId: number) => {
    try {
      setLoading(true);
      const mappings = await apiAdmin.listFieldMappings(apiId);
      setFieldMappings(mappings);
    } catch (err) {
      setError('Failed to load field mappings');
      console.error('Error loading field mappings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMapping = () => {
    setEditingMapping({
      originalFieldName: '',
      displayFieldName: '',
      fieldType: 'string',
      isRequired: false,
      order: fieldMappings.length + 1
    });
    setIsCreating(true);
  };

  const handleEditMapping = (mapping: FieldMapping) => {
    setEditingMapping(mapping);
    setIsCreating(false);
  };

  const handleDeleteMapping = async (mappingId: number) => {
    if (!window.confirm('Are you sure you want to delete this field mapping?')) {
      return;
    }

    try {
      setLoading(true);
      await apiAdmin.deleteFieldMapping(mappingId);
      await loadFieldMappings(selectedApiId as number);
    } catch (err) {
      setError('Failed to delete field mapping');
      console.error('Error deleting field mapping:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMapping = async (mapping: FieldMapping) => {
    if (!selectedApiId) {
      setError('Please select an API first');
      return;
    }

    try {
      setLoading(true);
      const mappingData = {
        ...mapping,
        api: selectedApiId
      };

      if (isCreating) {
        await apiAdmin.createFieldMapping(mappingData);
      } else if (mapping.id) {
        await apiAdmin.updateFieldMapping(mapping.id, mappingData);
      }

      setEditingMapping(null);
      setIsCreating(false);
      await loadFieldMappings(selectedApiId as number);
    } catch (err) {
      setError('Failed to save field mapping');
      console.error('Error saving field mapping:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMapping(null);
    setIsCreating(false);
  };

  const getFieldTypeColor = (type: string) => {
    switch (type) {
      case 'string': return 'info';
      case 'number': return 'success';
      case 'boolean': return 'warning';
      case 'array': return 'default';
      case 'object': return 'error';
      default: return 'default';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Field Mapping Manager</h2>
        {onClose && (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* API Selection */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select API</h3>
          <Select
            value={selectedApiId}
            onChange={(e) => setSelectedApiId(e.target.value ? Number(e.target.value) : '')}
            className="w-full"
          >
            <option value="">Choose an API</option>
            {apis.map(api => (
              <option key={api.id} value={api.id}>
                {api.name} ({api.status})
              </option>
            ))}
          </Select>
        </div>
      </Card>

      {selectedApiId && (
        <>
          {/* Field Mappings List */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Field Mappings</h3>
                <Button onClick={handleCreateMapping} disabled={loading}>
                  Add Field Mapping
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">Loading...</p>
                </div>
              ) : fieldMappings.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">No field mappings found for this API</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {fieldMappings.map((mapping, index) => (
                    <div key={mapping.id || index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {mapping.originalFieldName} → {mapping.displayFieldName}
                            </span>
                            <Badge variant={getFieldTypeColor(mapping.fieldType)}>
                              {mapping.fieldType}
                            </Badge>
                            {mapping.isRequired && (
                              <Badge variant="error">Required</Badge>
                            )}
                            <span className="text-sm text-gray-500 dark:text-gray-400">Order: {mapping.order}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditMapping(mapping)}
                            disabled={loading}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => mapping.id && handleDeleteMapping(mapping.id)}
                            disabled={loading}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Edit/Create Form */}
          {editingMapping && (
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {isCreating ? 'Create Field Mapping' : 'Edit Field Mapping'}
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Original Field Name
                      </label>
                      <Input
                        type="text"
                        value={editingMapping.originalFieldName}
                        onChange={(e) => setEditingMapping({
                          ...editingMapping,
                          originalFieldName: e.target.value
                        })}
                        placeholder="e.g., phone_number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Display Field Name
                      </label>
                      <Input
                        type="text"
                        value={editingMapping.displayFieldName}
                        onChange={(e) => setEditingMapping({
                          ...editingMapping,
                          displayFieldName: e.target.value
                        })}
                        placeholder="e.g., Phone Number"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Field Type
                      </label>
                      <Select
                        value={editingMapping.fieldType}
                        onChange={(e) => setEditingMapping({
                          ...editingMapping,
                          fieldType: e.target.value as any
                        })}
                      >
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="boolean">Boolean</option>
                        <option value="array">Array</option>
                        <option value="object">Object</option>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Order
                      </label>
                      <Input
                        type="number"
                        value={editingMapping.order}
                        onChange={(e) => setEditingMapping({
                          ...editingMapping,
                          order: parseInt(e.target.value) || 0
                        })}
                        min="0"
                      />
                    </div>
                    
                    <div className="flex items-center">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingMapping.isRequired}
                          onChange={(e) => setEditingMapping({
                            ...editingMapping,
                            isRequired: e.target.checked
                          })}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Required</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 pt-4">
                    <Button
                      onClick={() => handleSaveMapping(editingMapping)}
                      disabled={loading || !editingMapping.originalFieldName || !editingMapping.displayFieldName}
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="outline" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}; 